import cn from 'classnames'
import { useRef } from 'react'
import React from 'react'
import type { NavLinkProps } from 'react-router-dom'
import { NavLink } from 'react-router-dom'

import { testProps } from 'utils/test/qaData'

import * as s from './Link.module.scss'
import { isExternalLink } from './utils'

const TEST_ELEMENT_LINK = 'link'

interface ILinkPropsKit extends Omit<NavLinkProps, 'children'> {
  name: string
  children: React.ReactNode | (({ isActive }: { isActive: boolean }) => React.ReactNode)
  disabled?: boolean
  fluid?: boolean
  styleType?: 'base' | 'free'
  showActive?: boolean
  testData?: string
  external?: boolean // Hotfix https://www.notion.so/3248-Defect-177-graphql-1ca5457f94c581899694d762143447e9
}

const Link = React.forwardRef<HTMLAnchorElement, ILinkPropsKit>(
  (
    {
      children,
      name,
      disabled,
      fluid,
      styleType = 'base',
      testData,
      className,
      external = false,
      showActive,
      ...defRest
    },
    ref,
  ) => {
    const testPropsLink = testProps({ el: TEST_ELEMENT_LINK, name, testData })
    const isActiveRef = useRef(false)
    const { style, ...rest } = defRest
    const cnRoot = cn(s.root, className, s[styleType], { [s.fluid]: fluid, [s.disabled]: disabled })

    if (disabled || !rest.to) {
      return (
        <span {...testPropsLink} {...rest} className={cnRoot} ref={ref}>
          {typeof children === 'function' ? children({ isActive: false }) : children}
        </span>
      )
    }

    const { externalLink, path } = isExternalLink(rest.to)

    if (externalLink || external) {
      const { to, ...props } = rest
      return (
        <a {...testPropsLink} {...props} className={cnRoot} href={path} ref={ref}>
          {typeof children === 'function' ? children({ isActive: false }) : children}
        </a>
      )
    }

    return (
      <NavLink
        {...testPropsLink}
        {...rest}
        // eslint-disable-next-line react/forbid-component-props
        className={(active) => {
          const isActive = Boolean(showActive && active)
          isActiveRef.current = isActive
          return cn(cnRoot, { [s.active]: isActive })
        }}
        ref={ref}
      >
        {typeof children === 'function' ? children({ isActive: isActiveRef.current }) : children}
      </NavLink>
    )
  },
)

Link.displayName = 'Link'

export default Link
